import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useUser } from "../../Context/UserContext";
import useAPI from "../../Hooks/useAPI";
import Header2 from "../../Components/Header2/Header2";
import { default as Footer } from "../../Components/Footer/FooterSmall";
import {
  faArrowLeft,
  faSort,
  faSortUp,
  faSortDown,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import "../../Components/Page/Page.css";
import "./UserManagement.css";
import { userInfo } from "os";

interface UserContract {
  signer: string;
  email: string;
  contractstatus: string;
  statusid: string;
  transactiondate: string;
  datasource: string;
  addedby: string;
  signertitle: string;
  legalname: string;
}

interface ContractType {
  contracttype: string;
}

interface ContractStatus {
  contractstatus: string;
}

export default function ContractStatus() {
  const [userContracts, setUserContracts] = useState<UserContract[]>([]);
  const [contractTypes, setContractTypes] = useState<ContractType[]>([]);
  const [contractStatuses, setContractStatuses] = useState<ContractStatus[]>(
    []
  );
  const [selectedContractType, setSelectedContractType] = useState("all");
  const [selectedContractStatus, setSelectedContractStatus] = useState("all");
  const [selectedName, setSelectedName] = useState("");
  const [selectedCompany, setSelectedCompany] = useState("");
  const [error, setError] = useState<string>("");
  const { user, hasRole } = useUser();
  const navigate = useNavigate();
  const { getContractTypes, getContractStatuses, getUserContractStatus } =
    useAPI();

  const [sortConfig, setSortConfig] = useState<{
    key: keyof UserContract | null;
    direction: "ascending" | "descending" | null;
  }>({
    key: null,
    direction: null,
  });

  const fetchUserContracts = useCallback(
    async (
      selType: string,
      selStatus: string,
      selName: string,
      selCompany: string
    ) => {
      try {
        //const selStatus = selectedContractStatus || "all";
        const response = await getUserContractStatus(
          selStatus,
          selType,
          selName,
          selCompany
        );
        if (response?.data) {
          setUserContracts(response.data);
        } else {
          throw new Error("Failed to fetch User Contracts");
        }
      } catch (err) {
        setError(
          err instanceof Error
            ? err.message
            : "An error occurred during fetchUserContracts"
        );
      }
    },
    []
  );

  const fetchContractTypes = useCallback(async () => {
    try {
      const response = await getContractTypes();
      if (response?.data) {
        setContractTypes(response.data);
      } else {
        throw new Error("Failed to fetch Contract Types");
      }
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "An error occurred during fetch ContractTypes"
      );
    }
  }, []);

  const fetchContractStatuses = useCallback(async (selType: string) => {
    try {
      const response = await getContractStatuses(selType);
      if (response?.data) {
        setContractStatuses(response.data);
      } else {
        throw new Error("Failed to fetch Contract Statuses");
      }
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "An error occurred during fetch ContractStatuses"
      );
    }
  }, []);

  useEffect(() => {
    // Check if user is logged in.
    if (!user) {
      navigate("/login");
      return;
    }

    // Check if user has administrator role
    if (!hasRole("administrator")) {
      navigate("/");
      return;
    }

    fetchContractTypes();
  }, []);

  useEffect(() => {
    fetchContractStatuses(selectedContractType);
  }, [selectedContractType]);

  useEffect(() => {
    fetchUserContracts(
      selectedContractType,
      selectedContractStatus,
      selectedName,
      selectedCompany
    );
  }, [
    selectedContractType,
    selectedContractStatus,
    selectedName,
    selectedCompany,
  ]);

  const handleSort = (key: keyof UserContract) => {
    let direction: "ascending" | "descending" | null = "ascending";

    if (sortConfig.key === key) {
      if (sortConfig.direction === "ascending") {
        direction = "descending";
      } else if (sortConfig.direction === "descending") {
        direction = null;
      }
    }

    setSortConfig({ key, direction });
  };

  const getSortedUsers = () => {
    if (!sortConfig.key || !sortConfig.direction) {
      return userContracts;
    }

    return [...userContracts].sort((a, b) => {
      if (a[sortConfig.key!].toLowerCase() < b[sortConfig.key!].toLowerCase()) {
        return sortConfig.direction === "ascending" ? -1 : 1;
      }
      if (a[sortConfig.key!].toLowerCase() > b[sortConfig.key!].toLowerCase()) {
        return sortConfig.direction === "ascending" ? 1 : -1;
      }
      return 0;
    });
  };

  const getSortIcon = (columnKey: keyof UserContract) => {
    if (sortConfig.key !== columnKey) {
      return <FontAwesomeIcon icon={faSort} />;
    }
    return sortConfig.direction === "ascending" ? (
      <FontAwesomeIcon icon={faSortUp} />
    ) : (
      <FontAwesomeIcon icon={faSortDown} />
    );
  };
  const getStatusStyle = (status: string) => {
    if (status.toLowerCase().includes("rejected")) {
      return { color: "red" };
    } else if (status.toLowerCase().includes("approved")) {
      return { color: "green" };
    } else if (status.toLowerCase().includes("pending")) {
      return { color: "orange" };
    } else {
      return {};
    }
  };
  return (
    <div className="generalPage">
      <Header2 />
      <div className="usermanagement-container">
        <div className="pageContainer">
          <div className="button-container">
            <button className="back-button" onClick={() => navigate("/")}>
              <FontAwesomeIcon icon={faArrowLeft} /> Back to Home
            </button>
          </div>

          <div className="usermanagement-header">
            <div className="usermanagement-actions">
              <h1 className="usermanagement-title">Contract Status</h1>
            </div>
          </div>
          <div>
            <label className="marginright">Company</label>
            <input
              type="text"
              name="entityFilter"
              value={selectedCompany}
              onChange={(e) => setSelectedCompany(e.target.value)}
            />
            <label className="marginright marginleft">Signer</label>
            <input
              type="text"
              name="nameFilter"
              value={selectedName}
              onChange={(e) => setSelectedName(e.target.value)}
            />
            <label className="marginright marginleft">Contract Type</label>
            <select
              onChange={(e) => setSelectedContractType(e.target.value)}
              value={selectedContractType}
            >
              <option value="all">-- All Contract Types --</option>
              {contractTypes.map((ct, ctindex) => (
                <option key={ctindex} value={ct.contracttype}>
                  {ct.contracttype}
                </option>
              ))}
            </select>
            <label className="marginright marginleft">Status</label>
            <select
              onChange={(e) => setSelectedContractStatus(e.target.value)}
              value={selectedContractStatus}
            >
              <option value="all">-- All Contract Statuses --</option>
              {contractStatuses.map((cs, csindex) => (
                <option key={csindex} value={cs.contractstatus}>
                  {cs.contractstatus}
                </option>
              ))}
            </select>
          </div>
          {error && <div className="error-message">{error}</div>}

          <div className="users-table-container">
            <table className="users-table fixed-table">
              <thead className="table-header">
                <tr>
                  <th
                    onClick={() => handleSort("legalname")}
                    className="sortable-header normal-column"
                  >
                    Company {getSortIcon("legalname")}
                  </th>
                  <th
                    onClick={() => handleSort("signer")}
                    className="sortable-header"
                  >
                    Signer {getSortIcon("signer")}
                  </th>
                  <th
                    onClick={() => handleSort("signertitle")}
                    className="sortable-header"
                  >
                    Title {getSortIcon("signertitle")}
                  </th>
                  <th
                    onClick={() => handleSort("email")}
                    className="sortable-header wide-column"
                  >
                    Email {getSortIcon("email")}
                  </th>

                  <th
                    onClick={() => handleSort("statusid")}
                    className="sortable-header normal-column"
                  >
                    Status {getSortIcon("statusid")}
                  </th>

                  <th
                    onClick={() => handleSort("addedby")}
                    className="sortable-header"
                  >
                    Status Changed By {getSortIcon("addedby")}
                  </th>
                  <th
                    onClick={() => handleSort("transactiondate")}
                    className="sortable-header"
                  >
                    Transaction Date {getSortIcon("transactiondate")}
                  </th>
                  <th
                    onClick={() => handleSort("datasource")}
                    className="sortable-header"
                  >
                    Source {getSortIcon("datasource")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {getSortedUsers().map((userContracts) => (
                  <tr key={userContracts.email} className="table-row">
                    <td>{userContracts.legalname}</td>
                    <td>{userContracts.signer}</td>
                    <td>{userContracts.signertitle}</td>
                    <td>{userContracts.email}</td>
                    <td style={getStatusStyle(userContracts.contractstatus)}>
                      {userContracts.contractstatus}
                    </td>
                    <td>{userContracts.addedby}</td>
                    <td>{userContracts.transactiondate}</td>
                    <td>{userContracts.datasource}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
